import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import MarkdownEditor from '~/components/Common/MarkdownEditor';
import MarkdownPreview from '~/components/Common/MarkdownPreview';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import Spinner from '~/components/Common/Spinner';

import RateGroupService, { CreateRateGroupBody, RateGroup } from '~/services/cruises/RateGroupService';

interface Props {
  vendorId: string;
  isFetchingList: boolean;
  rateGroups: Array<RateGroup>;
  setRateGroups: (rateGroups: Array<RateGroup>) => void;
}

function RateGroupsSection({ vendorId, isFetchingList, rateGroups, setRateGroups }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [rateGroupForm, setRateGroupForm] = useState<CreateRateGroupBody>({
    vendorId,
    name: '',
    description: '',
    isRefundable: false,
    termsAndConditions: '',
  });

  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();

  const isFormValid = useMemo(() => {
    return rateGroupForm.name && rateGroupForm.termsAndConditions;
  }, [rateGroupForm]);

  const isFormDirty = useMemo(() => {
    return editingId || rateGroupForm.name || rateGroupForm.description || rateGroupForm.termsAndConditions;
  }, [editingId, rateGroupForm]);

  const resetPackageForm = useCallback(() => {
    setEditingId(null);
    setRateGroupForm({
      vendorId,
      name: '',
      description: '',
      isRefundable: false,
      termsAndConditions: '',
    });
  }, [vendorId]);

  const handleSave = useCallback(async () => {
    if (isLoading || !isFormValid) return;

    setIsLoading(true);

    try {
      if (editingId) {
        await RateGroupService.update(editingId, {
          name: rateGroupForm.name,
          description: rateGroupForm.description,
          isRefundable: rateGroupForm.isRefundable,
          termsAndConditions: rateGroupForm.termsAndConditions,
        });

        const updatedList = rateGroups.map((rateGroup) =>
          rateGroup.id === editingId
            ? {
                ...rateGroup,
                name: rateGroupForm.name,
                description: rateGroupForm.description,
                isRefundable: rateGroupForm.isRefundable,
                termsAndConditions: rateGroupForm.termsAndConditions,
              }
            : rateGroup,
        );
        setRateGroups(updatedList);
      } else {
        const newRateGroup = await RateGroupService.create(rateGroupForm);
        setRateGroups([newRateGroup, ...rateGroups]);
      }

      enqueueSnackbar(editingId ? 'Rate group updated successfully' : 'Rate group created successfully', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar('Error saving rate group', { variant: 'error' });
    } finally {
      setIsLoading(false);
      resetPackageForm();
    }
  }, [rateGroupForm, resetPackageForm, editingId, enqueueSnackbar, rateGroups, setRateGroups, isFormValid, isLoading]);

  const handleEdit = useCallback(
    (rateGroup: RateGroup) => {
      setEditingId(rateGroup.id);
      setRateGroupForm({
        vendorId,
        name: rateGroup.name,
        description: rateGroup.description,
        isRefundable: rateGroup.isRefundable,
        termsAndConditions: rateGroup.termsAndConditions,
      });
    },
    [vendorId],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (isDeleting) return;

      const confirmed = await showConfirmDialog({
        title: 'Delete Rate Group?',
        description: `This will delete the rate group, are you sure you want to proceed?`,
      });

      if (!confirmed) {
        return;
      }

      setIsDeleting(true);

      try {
        await RateGroupService.deleteById(id);
        setRateGroups(rateGroups.filter((rateGroup) => rateGroup.id !== id));
        enqueueSnackbar('Rate group deleted', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Error deleting rate group', { variant: 'error' });
      } finally {
        setIsDeleting(false);
      }
    },
    [rateGroups, showConfirmDialog, enqueueSnackbar, setRateGroups, isDeleting],
  );

  // reset form when vendorId changes
  useEffect(() => {
    resetPackageForm();
  }, [vendorId, resetPackageForm]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Rate Groups
      </Typography>

      <Stack spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Name"
          value={rateGroupForm.name}
          onChange={(e) => setRateGroupForm({ ...rateGroupForm, name: e.target.value })}
          fullWidth
        />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Description
          </Typography>
          <MarkdownEditor
            value={rateGroupForm.description}
            onChange={(content) => setRateGroupForm({ ...rateGroupForm, description: content })}
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Terms and Conditions
          </Typography>
          <MarkdownEditor
            value={rateGroupForm.termsAndConditions}
            onChange={(content) => setRateGroupForm({ ...rateGroupForm, termsAndConditions: content })}
            placeholder="Enter terms and conditions..."
          />
        </Box>

        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
          <FormControl fullWidth>
            <FormControlLabel
              control={<Checkbox />}
              label="Cancellation Policy (Refundable)"
              checked={rateGroupForm.isRefundable}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setRateGroupForm({ ...rateGroupForm, isRefundable: event.target.checked });
              }}
            />
          </FormControl>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {isFormDirty && (
              <Button variant="outlined" onClick={resetPackageForm}>
                Cancel
              </Button>
            )}

            <Button variant="contained" onClick={handleSave} disabled={isLoading || !isFormValid}>
              {isLoading ? <Spinner size={15} inline /> : editingId ? 'Update' : 'Add'}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Terms and Conditions</TableCell>
            <TableCell>Cancellation Policy</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rateGroups.map((rateGroup) => (
            <TableRow key={rateGroup.id}>
              <TableCell>{rateGroup.name}</TableCell>
              <TableCell>
                <MarkdownPreview content={rateGroup.description || ''} />
              </TableCell>
              <TableCell>
                <MarkdownPreview content={rateGroup.termsAndConditions || ''} />
              </TableCell>
              <TableCell>{rateGroup.isRefundable ? 'Refundable' : 'Non-Refundable'}</TableCell>

              <TableCell align="right">
                <Button size="small" onClick={() => handleEdit(rateGroup)}>
                  Edit
                </Button>

                <Button size="small" color="error" onClick={() => handleDelete(rateGroup.id)}>
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}

          {!isFetchingList && rateGroups.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No rate groups found
              </TableCell>
            </TableRow>
          )}

          {isFetchingList && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Spinner size={15} inline />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

export default RateGroupsSection;
